package com.videoanalyzer.web.config

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.serialization.json.*

/**
 * Feature flags service using ConfigCat.
 *
 * This implementation uses direct HTTP fetch to ConfigCat's CDN,
 * which is simpler and works reliably with Kotlin/JS.
 */
object FeatureFlags {

    private val httpClient = HttpClient()
    private var isInitialized = false
    private var flagValues: Map<String, JsonElement> = emptyMap()

    // Cached flag values (updated on refresh)
    private var _maintenanceMode: Boolean = false
    private var _maintenanceMessage: String = "We're currently performing maintenance. Please check back soon."

    /**
     * Whether the app is in maintenance mode.
     * When true, show the maintenance page instead of the app.
     */
    val maintenanceMode: Boolean
        get() = _maintenanceMode

    /**
     * Custom maintenance message to display.
     */
    val maintenanceMessage: String
        get() = _maintenanceMessage

    /**
     * Initialize and fetch feature flags.
     * Call this once when the app starts.
     */
    suspend fun initialize() {
        if (isInitialized) return

        val sdkKey = Config.CONFIGCAT_SDK_KEY

        // Don't initialize if SDK key is not configured
        if (sdkKey.startsWith("YOUR_")) {
            println("[FeatureFlags] SDK key not configured - using defaults")
            isInitialized = true
            return
        }

        try {
            println("[FeatureFlags] Initializing with SDK key: ${sdkKey.take(10)}...")
            refresh()
            isInitialized = true
            println("[FeatureFlags] Initialized - maintenanceMode: $_maintenanceMode")
        } catch (e: Exception) {
            println("[FeatureFlags] Failed to initialize: ${e.message}")
            isInitialized = true // Mark as initialized to prevent retry loops
        }
    }

    /**
     * Refresh flag values from ConfigCat CDN.
     */
    suspend fun refresh() {
        val sdkKey = Config.CONFIGCAT_SDK_KEY
        if (sdkKey.startsWith("YOUR_")) return

        try {
            // ConfigCat CDN URL format (v6 format)
            val configUrl = "https://cdn-global.configcat.com/configuration-files/$sdkKey/config_v6.json"
            println("[FeatureFlags] Fetching from: $configUrl")

            val response: HttpResponse = httpClient.get(configUrl)
            val jsonString = response.body<String>()
            println("[FeatureFlags] Response: ${jsonString.take(200)}...")

            val json = Json.parseToJsonElement(jsonString).jsonObject

            // Parse feature flags from ConfigCat JSON v6 format
            // Structure: { "f": { "flagKey": { "v": { "b": true } } } }
            val settings = json["f"]?.jsonObject
            if (settings == null) {
                println("[FeatureFlags] No 'f' field in response")
                return
            }

            println("[FeatureFlags] Found ${settings.size} flags: ${settings.keys}")

            flagValues = settings.mapValues { (key, value) ->
                // v6 format: value is in "v" object with type key (b=bool, s=string, i=int, d=double)
                val v = value.jsonObject["v"]
                println("[FeatureFlags] Flag '$key' raw value: $v")

                when {
                    v is JsonObject -> {
                        v.jsonObject["b"] ?: v.jsonObject["s"] ?: v.jsonObject["i"] ?: v.jsonObject["d"] ?: JsonNull
                    }
                    v is JsonPrimitive -> v
                    else -> JsonNull
                }
            }

            println("[FeatureFlags] Parsed values: $flagValues")

            // Update cached values
            _maintenanceMode = getBooleanInternal("maintenanceMode", false)
            _maintenanceMessage = getStringInternal(
                "maintenanceMessage",
                "We're currently performing maintenance. Please check back soon."
            )

            println("[FeatureFlags] Final - maintenanceMode: $_maintenanceMode, message: $_maintenanceMessage")
        } catch (e: Exception) {
            println("[FeatureFlags] Failed to refresh: ${e.message}")
            e.printStackTrace()
        }
    }

    private fun getBooleanInternal(key: String, defaultValue: Boolean): Boolean {
        val value = flagValues[key]
        println("[FeatureFlags] getBooleanInternal('$key') = $value")
        if (value == null) return defaultValue
        return when (value) {
            is JsonPrimitive -> value.booleanOrNull ?: defaultValue
            else -> defaultValue
        }
    }

    private fun getStringInternal(key: String, defaultValue: String): String {
        val value = flagValues[key] ?: return defaultValue
        return when (value) {
            is JsonPrimitive -> value.contentOrNull ?: defaultValue
            else -> defaultValue
        }
    }

    /**
     * Get a boolean feature flag value.
     */
    fun getBoolean(key: String, defaultValue: Boolean = false): Boolean {
        return getBooleanInternal(key, defaultValue)
    }

    /**
     * Get a string feature flag value.
     */
    fun getString(key: String, defaultValue: String = ""): String {
        return getStringInternal(key, defaultValue)
    }
}
